import React, { useCallback, useMemo } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { NewFromSelectField } from '../../../../presentation';
import { TaskPriority } from '@converge/graphql';

export interface PriorityDropdownProps {
  taskId: string;
  options: { value: string; label: string }[];
  onChange: (taskId: string, value: string) => void;
  initialValue?: string;
  disabled?: boolean;
  isNotActive?: boolean;
  handleUpdateTaskPriority?: (
    taskId: string,
    value: TaskPriority
  ) => Promise<void>;
}

export const PriorityDropdown = ({
  taskId,
  options,
  onChange,
  initialValue,
  disabled = false,
  isNotActive,
  handleUpdateTaskPriority,
}: PriorityDropdownProps) => {
  // Get form context to access current priority value
  const { getValues, setValue } = useFormContext();

  // Create a stable reference to the onChange function for SelectChangeEvent
  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as TaskPriority;

      // Update the form value
      setValue(`priority-${taskId}`, value, { shouldDirty: true });

      // Call the local onChange handler to update component state
      onChange(taskId, value);

      // Call the API handler to update the task priority on the server if provided
      if (handleUpdateTaskPriority) {
        handleUpdateTaskPriority(taskId, value);
      }
    },
    [taskId, onChange, setValue, handleUpdateTaskPriority]
  );

  // Get the current value from the form state
  let currentValue = getValues(`priority-${taskId}`);

  if (currentValue === undefined || currentValue === null) {
    currentValue = initialValue || 'HIGH';
    // Set the default value in the form
    setValue(`priority-${taskId}`, currentValue, { shouldDirty: false });
  }

  // Get the task priority - respect empty strings for new tasks
  const taskPriority = useMemo(() => {
    const value = getValues(`priority-${taskId}`);
    return value !== undefined ? value : initialValue || 'HIGH';
  }, [getValues, taskId, initialValue]);

  return (
    <Box className="priority-dropdown-wrapper" sx={{ marginRight: '10px' }}>
      <NewFromSelectField
        name={`priority-${taskId}`}
        className="task-priority"
        options={options}
        hideLabel
        hideBorders={false}
        value={taskPriority}
        onChange={handleChange}
        disabled={disabled}
        sx={{
          width: '160px',
          // Ensure dropdown is always on top
          zIndex: 10,
          position: 'relative',
          // Make it look slightly disabled if needed
          opacity: disabled ? 0.7 : 1,

          '.MuiOutlinedInput-root': {
            height: '37px',
            '& fieldset': {
              borderColor: '#666666',
            },
            '&:hover fieldset': {
              borderColor: '#666666',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#23a892',
            },
            '&.Mui-disabled fieldset': {
              borderColor: isNotActive
                ? 'rgba(131, 130, 130, 0.55)'
                : '#939292ff',
            },
            '&.Mui-disabled': {
              '& .MuiSelect-select': {
                color: isNotActive ? 'rgba(131, 130, 130, 0.55)' : '#939292ff',
                '-webkit-text-fill-color': isNotActive
                  ? 'rgba(131, 130, 130, 0.55)'
                  : '#939292ff',
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default PriorityDropdown;
